import { withDependencies } from '@wix/thunderbolt-ioc'
import { FetchFn } from '@wix/thunderbolt-becky-types'
import { Catharsis, CatharsisArgs, CatharsisSymbol } from '@wix/thunderbolt-catharsis'
import { CSMStoreSymbol, CSMStoreApi } from 'ds-feature-viewer-manager-renderer-model-api'
import { AppDefinitionId, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import { AsyncRunAndReport } from '@wix/thunderbolt-commons'
import { PresetInvalidationApi } from '../types'

export type PresetData = {
	widgetId: string
	appDefinitionId: string
	presetId: string
}

export type PresetsDataList = Array<PresetData>

export const createFetchMemoization = (fetchFn: FetchFn) => {
	const fetchPromises: { [url: string]: Promise<Response> } = {}
	const memoizedFetch: FetchFn = (url: string) => {
		if (!fetchPromises[url]) {
			fetchPromises[url] = fetchFn(url)
		}
		return fetchPromises[url]
	}
	return memoizedFetch
}

const getComponentBaseUrl = ({
	csm,
	appDefinitionId,
	widgetId,
}: {
	csm: ViewerClientSpecMap
	widgetId: string
	appDefinitionId: AppDefinitionId
	// todo componentBaseUrl is the wrong field!! the use in "|| widgetId" is for the future when
	// the url would be taken this way - || (appData?.widgets[widgetId]?.componentModel?.componentUrl as string)
}) => (csm[appDefinitionId]?.appFields?.platform?.baseUrls?.componentBaseUrl as string) || widgetId

export const PresetsInvalidationApi = withDependencies(
	[CSMStoreSymbol, CatharsisSymbol],
	(csmStore: CSMStoreApi, catharsis: Catharsis): PresetInvalidationApi => {
		const getOnInvalidation = ({
			fetchFn,
			runAndReport,
		}: {
			fetchFn: FetchFn
			runAndReport: AsyncRunAndReport
		}) => {
			const fetchMemo = createFetchMemoization(fetchFn)
			const fetchPresetCss = ({ appDefinitionId, widgetId, presetId }: PresetData) => {
				const baseComponentUrl = getComponentBaseUrl({
					csm: (csmStore.get() as unknown) as ViewerClientSpecMap,
					appDefinitionId,
					widgetId,
				})

				return fetchMemo(`${baseComponentUrl}/${widgetId}/${presetId}`)
					.then((res) => {
						if (!res.ok) {
							throw new Error('Fetching preset CSS failed')
						}
						return res.text()
					})
					.catch((e) => {
						throw new Error(
							`Failed to fetch presets for widget: ${widgetId}, preset: ${presetId}. Original Error - ${e.message}`
						)
					})
			}

			const presetsMemo = new Set<string>()
			const onPresetInvalidation: CatharsisArgs['onInvalidation'] = (path, get) => {
				const [, aspect] = path
				if (aspect === 'presetIds') {
					const presetDataList = get<PresetsDataList | undefined>(path)
					if (!presetDataList) {
						return
					}
					if (presetDataList) {
						presetDataList.forEach((presetData) => {
							const presetKey = JSON.stringify(presetData)
							if (!presetsMemo.has(presetKey)) {
								presetsMemo.add(presetKey)
								const { appDefinitionId, widgetId, presetId } = presetData
								const presetStore = get(['presetsCss', 'widgets', widgetId])
								runAndReport('fetchPresetCss', () =>
									fetchPresetCss({ appDefinitionId, widgetId, presetId }).then((cssJSON) => {
										presetStore[presetId] = cssJSON
										catharsis.transaction((ops) => ops.updatePresetCss(widgetId, presetStore))
									})
								)
							}
						})
					}
				}
			}

			return onPresetInvalidation
		}

		return {
			getOnInvalidation,
		}
	}
)
